import { yup } from '@framework/validation/initYup.ts';
import { Decimal } from 'decimal.js-light';
import { FormBuilderMetaFlags } from '../../../../../validator/form-builder/flags/FormBuilderMetaFlags.ts';
import { SchemaBuilderWhenTags } from '../../../../../validator/form-builder/formBuilderSchemaRules.ts';
import { parseAdvancedRichTextToString } from '../../../../../validator/utils/parseAdvancedRichTextToString.ts';
import {
  type ICreateWidgetFormSchemaInput,
  type IParentWidgetScope,
} from '../../../IWidgetType.ts';
import { setComputedNumberTransform } from '../../computed/number/setComputedNumberTransform.ts';
import { setComputeStringTransform } from '../../computed/string/setComputedStringTransform.ts';
import {
  type IPaymentRowWidget,
  type PaymentRowWidgetFieldConfig,
} from './IPaymentRowWidget.ts';
import { PaymentRowLogic } from './PaymentRowLogic.ts';
import { setPaymentFieldSchemaLabel } from './utils/setPaymentFieldSchemaLabel.ts';

/**
 * Funzione di gestione della validità dinamica dei campi in
 * base al flag "incluso nel pagamento".
 */
function requiredIfIncludedFn() {
  return ([included]: boolean[], schema: yup.AnySchema) => {
    const meta = schema.meta?.() ?? {};

    if (meta.applyRules === false) return schema.notRequired();
    if (meta.enabled === false) return schema.notRequired();
    return included ? schema.required() : schema.notRequired();
  };
}

/**
 * Applica la logica di calcolo delle espressioni allo schema di validazione dei campi di una voce di costo.
 * In caso di currency bisogna fare un passo ulteriore perché il campo è un numero che viene però salvato come stringa.
 */
function getPaymentRowFieldFormSchema(
  field: PaymentRowWidgetFieldConfig,
  parentScope: IParentWidgetScope,
  baseSchema: yup.AnySchema,
  options?: {
    widgetCode?: string;
    isCurrency?: boolean;
  },
) {
  const schema = baseSchema
    .formBuilderFlags(
      FormBuilderMetaFlags.SkipLabel,
      FormBuilderMetaFlags.SkipDefaultValue,
    )
    // Imposto la validità dinamica nel metaWhen, che verrà poi richiamato
    // _dopo_ aver calcolato applyRules.
    .metaWhen(SchemaBuilderWhenTags.PostApplyRules, {
      when: `${options?.widgetCode}@included`,
      then: requiredIfIncludedFn(),
    });

  // I campi non modificabili sono campi calcolati.
  if (!field.editable) {
    // Le currency necessitano di un passo ulteriore per la trasformazione.
    // devono essere trasformate perché sono calcolate come numbers ma salvate come stringhe.
    if (options?.isCurrency) {
      return setComputedNumberTransform(field, parentScope, schema, value =>
        value?.toString(),
      );
    }
    if (baseSchema.type === 'number') {
      return setComputedNumberTransform(field, parentScope, schema);
    }
    if (baseSchema.type === 'string') {
      return setComputeStringTransform(field, parentScope, schema);
    }
  }

  return schema;
}

export type PaymentRowFieldKey =
  // Valori di input
  | 'description'
  | 'baseUnitAmount'
  | 'quantity'
  | 'unit'
  | 'vat'
  | 'discount'
  // Valori calcolati
  | 'unitAmount' // -> importo unitario
  | 'taxableAmount' // -> imponibile
  | 'vatAmount' // -> IVA sul totale
  | 'totalNoDiscountAmount' // -> totale senza sconto
  | 'totalAmount' // -> totale
  // Flag
  | 'included';

/**
 * Costruisce la chiave dell'oggetto di validazione di una voce di costo.
 */
export function getPaymentRowKey(
  widgetCode: string,
  field: PaymentRowFieldKey,
) {
  return `${widgetCode}@${field}`;
}

export function createPaymentRowFormSchema(
  input: ICreateWidgetFormSchemaInput<IPaymentRowWidget>,
) {
  const { widget } = input;

  const descriptionKey = getPaymentRowKey(widget.code, 'description');
  const baseUnitAmountKey = getPaymentRowKey(widget.code, 'baseUnitAmount');
  const quantityKey = getPaymentRowKey(widget.code, 'quantity');
  const unitKey = getPaymentRowKey(widget.code, 'unit');
  const vatKey = getPaymentRowKey(widget.code, 'vat');
  const discountKey = getPaymentRowKey(widget.code, 'discount');

  const result = {
    [descriptionKey]: getPaymentRowFieldFormSchema(
      widget.description,
      input.parentScope,
      yup.string(),
      { widgetCode: widget.code },
    ),

    [baseUnitAmountKey]: getPaymentRowFieldFormSchema(
      widget.baseUnitAmount,
      input.parentScope,
      yup.string().stringCurrency(),
      { widgetCode: widget.code, isCurrency: true },
    ),

    [quantityKey]: getPaymentRowFieldFormSchema(
      widget.quantity,
      input.parentScope,
      yup.number().default(1),
      { widgetCode: widget.code },
    ),

    [unitKey]: getPaymentRowFieldFormSchema(
      widget.unit,
      input.parentScope,
      yup.string(),
      {
        widgetCode: widget.code,
      },
    ),

    [vatKey]: getPaymentRowFieldFormSchema(
      widget.vat,
      input.parentScope,
      yup.number().default(22),
      { widgetCode: widget.code },
    ),

    [discountKey]: getPaymentRowFieldFormSchema(
      widget.discount,
      input.parentScope,
      yup.number().default(0),
      { widgetCode: widget.code },
    ),

    [getPaymentRowKey(widget.code, 'included')]: yup
      .boolean()
      .required()
      .default(() => true)
      .transform(value => (widget.canBeExcluded ? value : true)),

    /**
     * RIEPILOGO
     */

    // Totale (che è definito come valore di base del widget)
    [widget.code]: yup
      .string()
      .default(null)
      .when(
        [baseUnitAmountKey, quantityKey, vatKey, discountKey],
        ([baseUnitAmount, quantity, vat, discount]: any[], schema) => {
          const unitAmount = PaymentRowLogic.getUnitAmount({
            baseUnitAmount,
            discount,
          });

          const taxable = PaymentRowLogic.getTaxableAmount({
            unitAmount,
            quantity,
          });

          const vatAmount = PaymentRowLogic.getVatAmount({ taxable, vat });

          const total = PaymentRowLogic.getTotalAmount({ taxable, vatAmount });

          if (!total) return schema.transform(() => null);

          return schema.transform(() => total.toString());
        },
      )
      .notRequired(),

    // Totale completo (ignorando lo sconto)
    [getPaymentRowKey(widget.code, 'totalNoDiscountAmount')]: yup
      .string()
      .default(null)
      .when(
        [baseUnitAmountKey, quantityKey, vatKey],
        ([baseUnitAmount, quantity, vat]: any[], schema) => {
          const taxable = PaymentRowLogic.getTaxableAmount({
            unitAmount:
              baseUnitAmount == null ? null : new Decimal(baseUnitAmount),
            quantity,
          });

          const vatAmount = PaymentRowLogic.getVatAmount({ taxable, vat });

          const total = PaymentRowLogic.getTotalAmount({ taxable, vatAmount });

          if (!total) return schema.transform(() => null);

          return schema.transform(() => total.toString());
        },
      )
      .formBuilderFlags(FormBuilderMetaFlags.SkipLabel)
      .notRequired(),

    // Prezzo Unitario (applicato lo sconto)
    [getPaymentRowKey(widget.code, 'unitAmount')]: yup
      .string()
      .default(null)
      .when(
        [baseUnitAmountKey, discountKey],
        ([baseUnitAmount, discount]: any[], schema) => {
          const unitAmount = PaymentRowLogic.getUnitAmount({
            baseUnitAmount,
            discount,
          });

          if (!unitAmount) return schema.transform(() => null);

          return schema.transform(() => unitAmount.toString());
        },
      )
      .formBuilderFlags(FormBuilderMetaFlags.SkipLabel)
      .notRequired(),

    // Imponibile
    [getPaymentRowKey(widget.code, 'taxableAmount')]: yup
      .string()
      .default(null)
      .when(
        [baseUnitAmountKey, quantityKey, discountKey],
        ([baseUnitAmount, quantity, discount]: any[], schema) => {
          const unitAmount = PaymentRowLogic.getUnitAmount({
            baseUnitAmount,
            discount,
          });

          const taxable = PaymentRowLogic.getTaxableAmount({
            unitAmount,
            quantity,
          });

          if (!taxable) return schema.transform(() => null);

          return schema.transform(() => taxable.toString());
        },
      )
      .formBuilderFlags(FormBuilderMetaFlags.SkipLabel)
      .notRequired(),

    // IVA
    [getPaymentRowKey(widget.code, 'vatAmount')]: yup
      .string()
      .default(null)
      .when(
        [baseUnitAmountKey, quantityKey, vatKey, discountKey],
        ([baseUnitAmount, quantity, vat, discount]: any[], schema) => {
          const unitAmount = PaymentRowLogic.getUnitAmount({
            baseUnitAmount,
            discount,
          });

          const taxable = PaymentRowLogic.getTaxableAmount({
            unitAmount,
            quantity,
          });
          const vatAmount = PaymentRowLogic.getVatAmount({ taxable, vat });

          if (!vatAmount) return schema.transform(() => null);

          return schema.transform(() => vatAmount.toString());
        },
      )
      .formBuilderFlags(FormBuilderMetaFlags.SkipLabel)
      .notRequired(),
  };

  // Andiamo ad impostare le label dei campi.
  setPaymentFieldSchemaLabel(
    result,
    parseAdvancedRichTextToString(widget.label, widget.code),
  );

  return result;
}
